import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { FiLogIn } from 'react-icons/fi';
import { Link, useHistory } from 'react-router-dom';
import { Box, Slide, Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import { useTheme } from '@material-ui/core/styles';
import useStyles from './styles';

import { useAuth } from '../../hooks/auth';

import getValidationErrors, { IErrors } from '../../utils/getValidationErrors';

import BarberShopTitle from '../../components/BarberShopTitle';
import CustomForm from '../../components/CustomForm';
import { form, initialData } from './form';
import { ESignUpStep } from '../../shared/interfaces/user-interfaces';
import LoadingButton from '../../components/LoadingButton';

interface SignInFormData {
  email: string;
  password: string;
}

const SignIn: React.FC = () => {
  const classes = useStyles();
  const formRef = useRef<FormHandles>(null);

  const { signIn, user } = useAuth();
  const history = useHistory();
  const theme = useTheme();

  const [errors, setErrors] = useState<IErrors>(initialData);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState<SignInFormData>(initialData);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string()
            .required('Email é obrigatório')
            .email('Digite um email válido'),
          password: Yup.string().min(6, 'No mínimo 6 dígitos'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await signIn({
          email: data.email,
          password: data.password,
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorsValidate = getValidationErrors(err);

          setErrors(errorsValidate);

          return;
        }

        toast.error('Ocorreu um erro ao fazer login, cheque as credenciais.');
      } finally {
        setLoading(false);
      }
    },
    [data, signIn],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        [e.target.name]: e.target.value,
      });
      setErrors({
        ...errors,
        [e.target.name]: '',
      });
    },
    [data, errors],
  );

  useEffect(
    function verifySignupStep() {
      if (user?.signup_step === ESignUpStep.FINISH) {
        history.push('/dashboard');
      } else if (user?.signup_step === ESignUpStep.CREATE_COMPANY) {
        history.push('/cadastro/negocio');
      } else if (user?.signup_step === ESignUpStep.CHOOSE_PLAN) {
        history.push('/cadastro/planos');
      } else {
        history.push('/');
      }
    },
    [history, user],
  );

  return (
    <Box
      display="flex"
      justifyContent="center"
      maxWidth="600px"
      margin="auto"
      p={2}
      height="100vh"
    >
      <Slide in direction="right">
        <Box
          pt={3}
          pb={3}
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="center"
          textAlign="center"
          className={classes.root}
          height="80%"
        >
          <BarberShopTitle title="Agendfly" />
          <Typography variant="h5" className={classes.title}>
            Faça seu login
          </Typography>
          <form onSubmit={handleSubmit}>
            <CustomForm
              onChange={handleChange}
              data={data}
              form={form}
              errors={errors}
            />
            <LoadingButton
              loading={loading}
              variant="contained"
              color="primary"
              className={classes.submitButton}
              type="submit"
              size="large"
              fullWidth
            >
              Entrar
            </LoadingButton>
            <Box marginTop={3}>
              Esqueceu sua senha?
              <Link
                to="/recuperar-senha"
                style={{
                  color: theme.palette.primary.main,
                  marginLeft: theme.spacing(1),
                  textDecoration: 'none',
                }}
              >
                Recuperar senha
              </Link>
            </Box>
          </form>

          <Link to="/cadastro">
            <FiLogIn />
            Criar conta
          </Link>
        </Box>
      </Slide>
    </Box>
  );
};

export default SignIn;
